import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Loader2, KeyRound, ArrowUpRight, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { API } from "../context/SiteContext";

export default function AdminResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => (params.get("token") || "").trim(), [params]);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("Missing reset token. Request a new link.");
      return;
    }
    if (password.length < 8) {
      toast.error("Use at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password,
      });
      toast.success("Password updated. Please sign in.");
      navigate("/admin", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Reset failed. Try again.";
      toast.error(typeof msg === "string" ? msg : "Reset failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <div className="mb-12 text-center">
          <p className="overline text-[#9F5C45] mb-4">Set a new password</p>
          <h1 className="font-serif font-light text-[#1C1917] text-4xl md:text-5xl tracking-tight">
            Choose a new password.
          </h1>
          <p className="text-[#57534E] mt-4">
            Minimum 8 characters. You'll be asked to sign in with the new password after this.
          </p>
        </div>

        {!token ? (
          <div
            data-testid="reset-password-no-token"
            className="bg-white border border-[#E2DCD0] p-10 text-center space-y-4"
          >
            <AlertTriangle className="w-10 h-10 text-[#9F5C45] mx-auto" />
            <h2 className="font-serif text-2xl text-[#1C1917]">Missing reset token.</h2>
            <p className="text-sm text-[#57534E] leading-relaxed">
              This link looks incomplete. Request a fresh reset email.
            </p>
            <Link
              to="/admin/forgot"
              data-testid="reset-password-request-new"
              className="inline-flex items-center gap-2 text-sm text-[#3A5A40] hover:text-[#1C1917] transition-colors pt-2"
            >
              Request a new link →
            </Link>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="bg-white border border-[#E2DCD0] p-10 space-y-7"
            data-testid="reset-password-form"
          >
            <label className="block">
              <span className="overline text-[#57534E] mb-2 block">New password</span>
              <input
                data-testid="reset-password-new"
                type="password"
                className="lgw-field"
                placeholder="At least 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
              />
            </label>
            <label className="block">
              <span className="overline text-[#57534E] mb-2 block">Confirm password</span>
              <input
                data-testid="reset-password-confirm"
                type="password"
                className="lgw-field"
                placeholder="Retype it"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </label>

            <button
              type="submit"
              disabled={submitting}
              data-testid="reset-password-submit"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 btn-primary text-sm tracking-wide disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Updating
                </>
              ) : (
                <>
                  <KeyRound className="w-4 h-4" /> Update password <ArrowUpRight className="w-4 h-4" />
                </>
              )}
            </button>

            <p className="text-xs text-[#57534E] text-center pt-2">
              Link is one-time use · expires 30 minutes after being sent
            </p>
          </form>
        )}

        <p className="text-center mt-8">
          <Link to="/admin" className="text-sm text-[#57534E] hover:text-[#3A5A40] transition-colors">
            ← Back to sign in
          </Link>
        </p>
      </div>
    </main>
  );
}
